import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  LineChart,
  Line,
  Legend,
  Cell,
} from "recharts";
import { 
  FiCalendar, 
  FiDownload, 
  FiBarChart2, 
  FiUsers, 
  FiMapPin,
  FiClock,
  FiTrendingUp
} from "react-icons/fi";
import { fetchVisaStats, fetchLocationStats, fetchUserHours } from "../api";

export default function EnterpriseAnalytics() {
  const [activeTab, setActiveTab] = useState("staff"); // staff, client, total
  const [viewMode, setViewMode] = useState("monthly"); // monthly, weekly
  const [chartType, setChartType] = useState("bar"); // bar, line
  const [dateRange, setDateRange] = useState({ start: "", end: "" });
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalHours, setTotalHours] = useState(0);
  const [showDatePicker, setShowDatePicker] = useState(false);

  // Color palette for different categories
  const colorPalette = [
    "#3B82F6", // blue
    "#10B981", // green
    "#F59E0B", // amber
    "#EF4444", // red
    "#8B5CF6", // purple
    "#EC4899", // pink
    "#14B8A6", // teal
    "#F97316", // orange
    "#6366F1", // indigo
    "#84CC16", // lime
  ];

  // Get color for specific index
  const getColor = (index) => colorPalette[index % colorPalette.length];

  // Initialize with current month
  useEffect(() => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1)
      .toISOString()
      .split("T")[0];
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0)
      .toISOString()
      .split("T")[0];
    console.log('=== INITIAL LOAD ===');
    console.log('Initial date range set:', start, 'to', end);
    console.log('Active tab:', activeTab);
    setDateRange({ start, end });
    
    // Fetch immediately with calculated dates
    fetchData(activeTab, start, end);
  }, []); // Only run once on mount

  // Fetch data based on active tab
  const fetchData = async (tab, start, end) => {
    setLoading(true);
    try {
      let result;
      console.log('=== FETCH DATA CALLED ===');
      console.log(`Tab: ${tab}, Start: ${start}, End: ${end}`);
      
      if (tab === "staff") {
        // Use exact logic from BarGraph.js
        const params = {};
        if (start && end) {
          params.start = start;
          params.end = end;
        }
        console.log('Calling fetchVisaStats with params:', params);
        const res = await fetchVisaStats(params);
        console.log('Staff hours API full response:', res);
        console.log('Staff hours API data:', res.data);
        console.log('Staff hours date range from backend:', res.data?.range);
        const rawData = res?.data?.data || [];
        console.log('Raw staff data from API:', rawData);
        console.log('Raw staff data details:', JSON.stringify(rawData, null, 2));
        
        result = rawData
          .map((d) => ({ 
            name: d._id || "Unknown",
            hours: parseFloat(d.totalHours) || 0,
            count: d.count || 0,
          }))
          .filter((d) => d.hours > 0 || d.count > 0);
        
        console.log('Processed staff data after filter:', result);
      } else if (tab === "client") {
        // Use exact logic from LocationGraph.js
        console.log('Calling fetchLocationStats with:', start, end);
        const res = await fetchLocationStats(start, end);
        console.log('Client hours API full response:', res);
        console.log('Client hours API data:', res.data);
        const rawData = res?.data?.data || [];
        console.log('Raw client data from API:', rawData);
        
        result = rawData
          .map((d) => ({
            name: d._id || "Unknown",
            hours: parseFloat(d.totalHours) || 0,
            count: d.count || 0,
          }))
          .filter((d) => d.hours > 0 || d.count > 0);
        
        console.log('Processed client data after filter:', result);
      } else {
        // Use exact logic from TotalHours.js
        const params = { startDate: start, endDate: end };
        console.log('Calling fetchUserHours with params:', params);
        const res = await fetchUserHours(params);
        console.log('Total hours API full response:', res);
        console.log('Total hours API data:', res.data);
        const employees = res.data?.employees || [];
        console.log('Employees from API:', employees);
        
        // Group by employee + location (same as TotalHours.js)
        const aggregated = Object.values(
          employees.reduce((acc, emp) => {
            const key = `${emp.employeeName}-${emp.locationName}`;
            if (!acc[key]) {
              acc[key] = { 
                name: emp.employeeName || "Unknown",
                hours: parseFloat(emp.workedHours) || 0,
                count: 1
              };
            } else {
              acc[key].hours += parseFloat(emp.workedHours) || 0;
              acc[key].count += 1;
            }
            return acc;
          }, {})
        );
        
        // Sort by hours in descending order
        result = aggregated.sort((a, b) => b.hours - a.hours);
        console.log('Aggregated total hours:', result);
      }
      
      console.log('=== FINAL CHART DATA ===');
      console.log('Data to display:', result);
      console.log('Data length:', result.length);
      console.log('Individual hours:', result.map(r => ({ name: r.name, hours: r.hours })));
      setData(result);
      const total = result.reduce((sum, item) => sum + (parseFloat(item.hours) || 0), 0);
      console.log('Total hours calculated:', total);
      console.log('Total hours breakdown:', result.reduce((acc, item) => `${acc} + ${item.hours}`, '0'));
      setTotalHours(total);
    } catch (error) {
      console.error("=== ERROR FETCHING DATA ===");
      console.error("Error:", error);
      console.error("Error response:", error.response?.data);
      console.error("Error message:", error.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle tab change
  const handleTabChange = (tab) => {
    console.log('Tab changed to:', tab, 'with date range:', dateRange);
    setActiveTab(tab);
    fetchData(tab, dateRange.start, dateRange.end);
  };

  // Handle view mode change
  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    const now = new Date();
    let start, end;

    if (mode === "weekly") {
      // Get Monday of current week
      const dayOfWeek = now.getDay(); // 0 = Sunday, 1 = Monday, etc.
      const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek; // If Sunday, go back 6 days
      const monday = new Date(now);
      monday.setDate(now.getDate() + mondayOffset);
      monday.setHours(0, 0, 0, 0);
      
      // Get Sunday of current week
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);
      sunday.setHours(23, 59, 59, 999);
      
      start = monday.toISOString().split("T")[0];
      end = sunday.toISOString().split("T")[0];
      
      console.log('Weekly range calculated:', { start, end, monday, sunday });
    } else {
      // Monthly
      start = new Date(now.getFullYear(), now.getMonth(), 1)
        .toISOString()
        .split("T")[0];
      end = new Date(now.getFullYear(), now.getMonth() + 1, 0)
        .toISOString()
        .split("T")[0];
      
      console.log('Monthly range calculated:', { start, end });
    }

    setDateRange({ start, end });
    fetchData(activeTab, start, end);
  };

  // Handle custom date apply
  const handleApplyDates = () => {
    if (dateRange.start && dateRange.end) {
      fetchData(activeTab, dateRange.start, dateRange.end);
      setShowDatePicker(false);
    }
  };

  // Download report
  const handleDownload = () => {
    if (!data.length) {
      alert("No data available to download");
      return;
    }

    const headers = ["Category", "Total Hours", "Count"];
    const rows = data.map((item) => [
      item.name,
      item.hours.toFixed(2),
      item.count,
    ]);

    const csv = [headers, ...rows].map((row) => row.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${activeTab}_analytics_${dateRange.start}_to_${dateRange.end}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const tabs = [
    { id: "staff", label: "Staff Hours", icon: FiUsers },
    { id: "client", label: "Client Hours", icon: FiMapPin },
    { id: "total", label: "Total Hours", icon: FiClock },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-gray-200/50 overflow-hidden"
    >
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm">
              <FiTrendingUp className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">Analytics Dashboard</h2>
              <p className="text-blue-100 text-sm">Comprehensive hours breakdown & insights</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-blue-100 text-sm font-medium">Total Hours:</span>
            <div className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-xl">
              <span className="text-white text-xl font-bold">{Math.round(totalHours)}h</span>
            </div>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="p-6 border-b border-gray-200">
        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-6">
          {tabs.map((tab) => (
            <motion.button
              key={tab.id}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => handleTabChange(tab.id)}
              className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all ${
                activeTab === tab.id
                  ? "bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              <tab.icon className="w-4 h-4" />
              <span>{tab.label}</span>
            </motion.button>
          ))}
        </div>

        {/* View Mode & Chart Type */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleViewModeChange("monthly")}
              className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                viewMode === "monthly"
                  ? "bg-blue-600 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => handleViewModeChange("weekly")}
              className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                viewMode === "weekly"
                  ? "bg-blue-600 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              Weekly
            </button>
          </div>

          <div className="flex items-center gap-2">
            {/* Custom Date Picker */}
            <div className="relative">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowDatePicker(!showDatePicker)}
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700 font-medium text-sm transition-all border border-gray-300"
              >
                <FiCalendar className="w-4 h-4" />
                <span>Custom Range</span>
              </motion.button>

              <AnimatePresence>
                {showDatePicker && (
                  <motion.div
                    initial={{ opacity: 0, y: -10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -10, scale: 0.95 }}
                    className="absolute right-0 top-full mt-2 bg-white rounded-xl shadow-2xl border border-gray-200 p-4 z-50 min-w-[320px]"
                  >
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Start Date
                        </label>
                        <input
                          type="date"
                          value={dateRange.start}
                          onChange={(e) =>
                            setDateRange({ ...dateRange, start: e.target.value })
                          }
                          className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          End Date
                        </label>
                        <input
                          type="date"
                          value={dateRange.end}
                          onChange={(e) =>
                            setDateRange({ ...dateRange, end: e.target.value })
                          }
                          className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                        />
                      </div>
                      <button
                        onClick={handleApplyDates}
                        className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white py-2 rounded-lg font-medium hover:shadow-lg transition-all"
                      >
                        Apply
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Chart Type Toggle */}
            <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
              <button
                onClick={() => setChartType("bar")}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                  chartType === "bar"
                    ? "bg-white text-blue-600 shadow"
                    : "text-gray-600"
                }`}
              >
                Bar
              </button>
              <button
                onClick={() => setChartType("line")}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                  chartType === "line"
                    ? "bg-white text-blue-600 shadow"
                    : "text-gray-600"
                }`}
              >
                Line
              </button>
            </div>

            {/* Download Button */}
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleDownload}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg font-medium hover:shadow-lg transition-all"
            >
              <FiDownload className="w-4 h-4" />
              <span className="hidden sm:inline">Report</span>
            </motion.button>
          </div>
        </div>

        {/* Date Range Display */}
        <div className="mt-4 flex items-center gap-2 text-sm text-gray-600">
          <FiCalendar className="w-4 h-4" />
          <span className="font-medium">
            {new Date(dateRange.start).toLocaleDateString("en-GB")} -{" "}
            {new Date(dateRange.end).toLocaleDateString("en-GB")}
          </span>
        </div>
      </div>

      {/* Chart Area */}
      <div className="p-6">
        {loading ? (
          <div className="h-96 flex items-center justify-center">
            <div className="flex flex-col items-center gap-3">
              <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
              <p className="text-gray-600 font-medium">Loading analytics...</p>
            </div>
          </div>
        ) : data.length === 0 ? (
          <div className="h-96 flex items-center justify-center">
            <div className="text-center">
              <FiBarChart2 className="w-16 h-16 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 font-medium">No data available for selected period</p>
            </div>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={400}>
            {chartType === "bar" ? (
              <BarChart data={data}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis
                  dataKey="name"
                  tick={{ fill: "#6B7280", fontSize: 12 }}
                  stroke="#9CA3AF"
                />
                <YAxis tick={{ fill: "#6B7280", fontSize: 12 }} stroke="#9CA3AF" />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "rgba(255, 255, 255, 0.95)",
                    border: "1px solid #E5E7EB",
                    borderRadius: "12px",
                    boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
                  }}
                  cursor={{ fill: "rgba(59, 130, 246, 0.1)" }}
                />
                <Legend wrapperStyle={{ paddingTop: "20px" }} />
                <Bar
                  dataKey="hours"
                  radius={[8, 8, 0, 0]}
                  name="Total Hours"
                >
                  {data.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={getColor(index)} />
                  ))}
                </Bar>
              </BarChart>
            ) : (
              <LineChart data={data}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis
                  dataKey="name"
                  tick={{ fill: "#6B7280", fontSize: 12 }}
                  stroke="#9CA3AF"
                />
                <YAxis tick={{ fill: "#6B7280", fontSize: 12 }} stroke="#9CA3AF" />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "rgba(255, 255, 255, 0.95)",
                    border: "1px solid #E5E7EB",
                    borderRadius: "12px",
                    boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
                  }}
                />
                <Legend wrapperStyle={{ paddingTop: "20px" }} />
                <Line
                  type="monotone"
                  dataKey="hours"
                  stroke={colorPalette[0]}
                  strokeWidth={3}
                  dot={{ fill: colorPalette[0], r: 5 }}
                  activeDot={{ r: 7 }}
                  name="Total Hours"
                />
              </LineChart>
            )}
          </ResponsiveContainer>
        )}

        {/* Summary Cards */}
        {!loading && data.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-xl border border-blue-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-600 text-sm font-medium">Total Categories</p>
                  <p className="text-2xl font-bold text-blue-900">{data.length}</p>
                </div>
                <FiBarChart2 className="w-8 h-8 text-blue-600" />
              </div>
            </div>
            <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-xl border border-green-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-600 text-sm font-medium">Average Hours</p>
                  <p className="text-2xl font-bold text-green-900">
                    {(totalHours / data.length).toFixed(2)}
                  </p>
                </div>
                <FiClock className="w-8 h-8 text-green-600" />
              </div>
            </div>
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded-xl border border-purple-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-600 text-sm font-medium">Peak Hours</p>
                  <p className="text-2xl font-bold text-purple-900">
                    {Math.max(...data.map((d) => d.hours)).toFixed(2)}
                  </p>
                </div>
                <FiTrendingUp className="w-8 h-8 text-purple-600" />
              </div>
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
}
